import { FC, MouseEvent, ReactNode } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Tooltip,
} from '@mui/material'
import { theme } from '@/theme'

type CustomLinkNavigationProps = {
  icon: ReactNode
  label: string
  link: string
  isExternal?: boolean
  /** If isExternal is true, open in a new tab/window. Default: true for external links, can be overridden to false. */
  openInNewTab?: boolean
  primaryColor?: boolean
  isCollapsed?: boolean
  onClick?: never
}

type CustomLinkActionProps = {
  icon: ReactNode
  label: string
  link?: never
  isExternal?: boolean
  openInNewTab?: boolean
  primaryColor?: boolean
  isCollapsed?: boolean
  onClick: () => void
}

type CustomLinkProps = CustomLinkNavigationProps | CustomLinkActionProps

export const CustomLink: FC<CustomLinkProps> = ({
  icon,
  label,
  link,
  isExternal,
  openInNewTab: openInNewTabProp,
  primaryColor,
  isCollapsed = false,
  onClick,
}) => {
  const location = useLocation()
  // Only attempt to mark active for internal (router) links
  const isActive =
    !!link &&
    !isExternal &&
    link.indexOf(
      location.pathname === '/' ? 'dashboard' : location.pathname,
    ) !== -1

  const handleClick = (
    event: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLAnchorElement>,
  ) => {
    if (!link && onClick) {
      event.preventDefault()
      onClick()
    }
  }

  const colorStyle = primaryColor
    ? {
        color: 'white',
        '&:hover': {
          color: 'white',
        },
      }
    : { color: isActive ? 'primary.main' : 'grey.600' }

  // Decide which underlying component to use:
  // - Internal navigation: RouterLink
  // - External URL: 'a'
  // - Action (no link): 'button'
  const componentType: any = link ? (isExternal ? 'a' : RouterLink) : 'button'

  // Determine if we should open in new tab (defaults to true for external links unless explicitly set false)
  const shouldOpenInNewTab = !!isExternal && (openInNewTabProp ?? true)

  const linkButton = (
    <ListItemButton
      component={componentType}
      {...(!isExternal && link ? { to: link } : {})}
      {...(isExternal && link ? { href: link } : {})}
      {...(isExternal && link && shouldOpenInNewTab
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {})}
      // Open external links in same window (no target _blank)
      sx={{
        py: 0.75,
        px: isCollapsed ? 1 : 2,
        borderRadius: 1,
        mx: isCollapsed ? 0.5 : 0.5,
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        minHeight: 40,
        '&.Mui-selected': {
          backgroundColor: 'rgba(61, 90, 254, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(61, 90, 254, 0.12)',
          },
        },
        ...(primaryColor && {
          bgcolor: theme.palette.primary.main,
          '&:hover': {
            bgcolor: theme.palette.primary.main,
          },
        }),
      }}
      selected={isActive}
      onClick={handleClick}
    >
      <ListItemIcon
        sx={{
          minWidth: isCollapsed ? 24 : 32,
          margin: isCollapsed ? '0 auto' : 0,
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          ...colorStyle,
        }}
      >
        {icon}
      </ListItemIcon>
      {!isCollapsed && (
        <ListItemText
          primary={label}
          slotProps={{
            primary: {
              fontSize: '0.875rem',
              fontWeight: 700,
              fontFamily: 'Mulish, sans-serif',
              ...colorStyle,
            },
          }}
        />
      )}
    </ListItemButton>
  )

  return (
    <ListItem disablePadding>
      {isCollapsed ? (
        <Tooltip title={label} placement="right">
          {linkButton}
        </Tooltip>
      ) : (
        linkButton
      )}
    </ListItem>
  )
}
